import { useState, type FormEvent } from "react";

const FORM_ID = "dahg42bp14a1h3mr6lkg";
const FORM_ACTION = "https://readdy.ai/api/form/dahg42bp14a1h3mr6lkg";

type Status = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

interface NewsletterFormProps {
  tone?: "light" | "ink";
}

export default function NewsletterForm({ tone = "ink" }: NewsletterFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const isLight = tone === "light";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setError("");
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value !== "string") return;
      const trimmed = value.trim();
      if (!trimmed) return;
      payload.append(key, trimmed);
    });

    setStatus("submitting");
    setError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const okCode = parsed?.code === "OK";

      if (response.ok && okCode && !isSpam) {
        setStatus("success");
        setError("");
        form.reset();
      } else {
        setStatus("error");
        setError(serverMsg || "提交未成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setError("网络异常，请稍后重试。");
    }
  };

  const labelClass = isLight ? "text-background-200" : "text-foreground-700";
  const fieldClass = isLight
    ? "w-full rounded-md border border-background-50/25 bg-background-50/10 px-3.5 py-2.5 text-sm text-background-50 placeholder:text-background-200/50 focus:border-accent-400 focus:outline-none"
    : "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      action={FORM_ACTION}
      method="post"
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="sub-email" className={`mb-1.5 block text-xs font-medium ${labelClass}`}>
            邮箱地址
          </label>
          <input
            id="sub-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor="sub-interest" className={`mb-1.5 block text-xs font-medium ${labelClass}`}>
            最关注的栏目
          </label>
          <select id="sub-interest" name="interest" className={fieldClass} defaultValue="直播同框">
            <option value="直播同框">直播同框</option>
            <option value="品牌代言">品牌代言</option>
            <option value="综艺同台">综艺同台</option>
            <option value="时尚红毯">时尚红毯</option>
            <option value="影视选角">影视选角</option>
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="sub-message" className={`mb-1.5 block text-xs font-medium ${labelClass}`}>
          想看的话题或线索（选填，最多 500 字）
        </label>
        <textarea
          id="sub-message"
          name="message"
          rows={3}
          maxLength={500}
          placeholder="例如：想看看同一位艺人不同阶段的代言层级变化"
          className={`${fieldClass} resize-none`}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <span className={isLight ? "text-[11px] text-background-200/70" : "text-[11px] text-foreground-500"}>
          我们仅用于内容推送与选题反馈，不会对外提供你的邮箱。
        </span>
        <button
          type="submit"
          disabled={status === "submitting"}
          className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 ${
            isLight
              ? "bg-accent-500 text-secondary-950 hover:bg-accent-400"
              : "bg-primary-500 text-background-50 hover:bg-primary-600"
          }`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={status === "submitting" ? "ri-loader-4-line text-base" : "ri-send-plane-line text-base"} />
          </span>
          {status === "submitting" ? "提交中…" : "订阅并提交"}
        </button>
      </div>

      <div className="field-decoy-extra" aria-hidden="true">
        <label htmlFor="sub-website-alt">Website</label>
        <input
          id="sub-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "success" ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-checkbox-circle-line text-base" />
          </span>
          提交成功，感谢你的订阅，我们会把新一期解读发到你的邮箱。
        </p>
      ) : null}
      {status === "error" ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-primary-600">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line text-base" />
          </span>
          {error}
        </p>
      ) : null}
    </form>
  );
}